import { useTheme } from 'emotion-theming';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import Highlighter from 'react-highlight-words';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import DataGrid, { Cell, Column, HeaderCell } from '@veeva/corgix/data-grid';
import Icon from '@veeva/corgix/icon';
import Link from '@veeva/corgix/link';
import { useEffect, useState } from 'react';
import ResponsiveTooltip from '../../components/ResponsiveTooltip';
import buildAndSubmitForm from '../../services/buildAndSubmitForm';
import useMessageService from '../../services/i18n/useMessageService';
import getLastLoginContent from './getLastLoginContent';
import { userGeneralInfo } from '../my-account/myAccountApi';

const defaultSort = ['lastLogin', 'desc'];

function getInitialSort() {
    try {
        const item = localStorage.getItem('ApplicationGrid.sort');
        const sort = JSON.parse(item);
        return sort || defaultSort;
    } catch (error) {
        return defaultSort;
    }
}

// filter and sort apps
function getDisplayedApps(apps, searchTerm, sortColumnKey, sortDir) {
    const filtered = apps.filter((r) => {
        return ['name', 'companyName', 'companyIdentifier'].some((searchableField) => {
            return r[searchableField].toLowerCase().includes(searchTerm.toLowerCase());
        });
    });
    const sorted = filtered.sort((a, b) => {
        if (a[sortColumnKey] < b[sortColumnKey]) {
            return -1;
        }
        if (a[sortColumnKey] > b[sortColumnKey]) {
            return 1;
        }
        return 0;
    });
    if (sortDir === 'desc') {
        sorted.reverse();
    }
    return sorted;
}

export default function ApplicationGrid({ applications, loading, searchTerm }) {
    const { darkGray } = useTheme();
    const [
        [
            applicationNameLabel,
            companyNameLabel,
            lastLoginLabel,
            companyIdentifierLabel,
            noData,
            noMatches,
        ],
    ] = useMessageService([
        'home.application_name',
        'home.company_name',
        'home.last_login',
        'home.company_identifier',
        'general.no_data_found',
        'general.no_matches_found',
    ]);

    const [locale, setLocale] = useState();
    useEffect(() => {
        (async () => {
            const res = await userGeneralInfo();
            setLocale(res.data.locale);
        })();
    }, []);

    const [sort, setSort] = useState(getInitialSort());
    const [sortColumnKey, sortDir] = sort;

    useEffect(() => {
        localStorage.setItem('ApplicationGrid.sort', JSON.stringify(sort));
    }, [sort]);

    const displayedApps = getDisplayedApps(applications, searchTerm, sortColumnKey, sortDir);

    const handleColumnSort = (columnKey, columnId, dir) => {
        setSort([columnKey, dir]);
    };

    const getColumnSortDirection = (columnKey) => {
        return sortColumnKey === columnKey ? sortDir : undefined;
    };

    const renderNameCell = ({ cellData, rowIndex }) => {
        const record = displayedApps[rowIndex];

        const handleLinkClick = () => {
            buildAndSubmitForm(record.appRedirectUrl);
        };

        return (
            <Cell>
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        height: 100%;
                    `}
                >
                    <Link onClick={handleLinkClick}>
                        <Highlighter
                            searchWords={[searchTerm]}
                            textToHighlight={cellData}
                            highlightTag="strong"
                        />
                    </Link>
                    {record.description && (
                        <ResponsiveTooltip
                            placement="bottom"
                            content={
                                <div
                                    css={css`
                                        word-wrap: break-word;
                                    `}
                                >
                                    {record.description}
                                </div>
                            }
                        >
                            <Icon type={faQuestionCircle} color={darkGray} />
                        </ResponsiveTooltip>
                    )}
                </div>
            </Cell>
        );
    };

    const renderLastLoginCell = ({ cellData, rowIndex }) => {
        const record = displayedApps[rowIndex];
        return (
            <Cell>
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        height: 100%;
                    `}
                >
                    {getLastLoginContent(record.lastLogin, record.lastLoginFormatted, locale)}
                </div>
            </Cell>
        );
    };

    const renderTextCell = ({ cellData }) => (
        <Cell>
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    height: 100%;
                `}
            >
                <Highlighter
                    css={css`
                        width: 100%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: block;
                    `}
                    searchWords={[searchTerm]}
                    textToHighlight={cellData}
                    highlightTag="strong"
                />
            </div>
        </Cell>
    );

    return (
        <DataGrid
            data={displayedApps}
            onColumnSort={handleColumnSort}
            loading={loading}
            rowKey="appRedirectUrl"
            spanFullGrid
            css={({ gray }) =>
                css`
                    border-top: 2px solid ${gray};
                `
            }
            emptyDataMessage={searchTerm ? noMatches : noData}
        >
            <Column
                columnKey="name"
                header={<HeaderCell>{applicationNameLabel}</HeaderCell>}
                cell={renderNameCell}
                sortDirection={getColumnSortDirection('name')}
                width={300}
                sortable
                resizable
            />
            <Column
                columnKey="companyName"
                header={<HeaderCell>{companyNameLabel}</HeaderCell>}
                cell={renderTextCell}
                sortDirection={getColumnSortDirection('companyName')}
                width={300}
                sortable
                resizable
            />
            <Column
                columnKey="lastLogin"
                header={<HeaderCell>{lastLoginLabel}</HeaderCell>}
                cell={renderLastLoginCell}
                sortDirection={getColumnSortDirection('lastLogin')}
                width={300}
                sortable
                resizable
            />
            <Column
                columnKey="companyIdentifier"
                header={<HeaderCell>{companyIdentifierLabel}</HeaderCell>}
                cell={renderTextCell}
                sortDirection={getColumnSortDirection('companyIdentifier')}
                width={300}
                sortable
                resizable
            />
        </DataGrid>
    );
}
ApplicationGrid.propTypes = {
    applications: PropTypes.arrayOf(
        PropTypes.shape({
            appRedirectUrl: PropTypes.string,
            companyIdentifier: PropTypes.string,
            companyName: PropTypes.string,
            description: PropTypes.string,
            lastLogin: PropTypes.instanceOf(Date),
            lastLoginFormatted: PropTypes.string,
            name: PropTypes.string,
        }),
    ),
    loading: PropTypes.bool,
    searchTerm: PropTypes.string,
};
